/**
 * exposure.js — Exposure Guard for the Bet Tracker (Winning Features
 * addendum). Pure, no I/O.
 *
 * The guard's job: warn when the same team sits on several of the
 * user's PENDING tickets, so one bad game can't quietly sink a whole
 * portfolio ("Titans are on 2 of your tickets").
 *
 * Matching rules (kept deliberately simple and explainable):
 * - A team is "on" a ticket when it is the homeTeam or awayTeam of
 *   any leg, or the backed side (leg.name) of a moneyline/spread leg.
 *   Totals legs name 'Over'/'Under', never a team, so leg.name only
 *   counts for h2h/spreads.
 * - Names match case-insensitively after trimming.
 * - A team is counted once per ticket no matter how many legs of
 *   that ticket involve it — the count is TICKETS, not legs.
 * - Only pending tickets count; settled bets are history.
 */

const normalizeTeamName = (name) => String(name || '').trim().toLowerCase();

/** Display names of every team on one stored bet record. */
export function teamsInBet(bet) {
  const seen = new Map(); // normalized -> display name
  const add = (name) => {
    const normalized = normalizeTeamName(name);
    if (normalized && !seen.has(normalized)) seen.set(normalized, String(name).trim());
  };
  for (const leg of (bet && bet.legs) || []) {
    add(leg.homeTeam);
    add(leg.awayTeam);
    if (leg.market !== 'totals') add(leg.name);
  }
  return [...seen.values()];
}

/**
 * Map of team -> { team, count, betIds } across the user's PENDING
 * tickets (count = distinct tickets the team appears on).
 */
export function countTicketsByTeam(bets) {
  const byTeam = new Map();
  for (const bet of bets || []) {
    if (!bet || bet.status !== 'pending') continue;
    for (const team of teamsInBet(bet)) {
      const key = normalizeTeamName(team);
      const entry = byTeam.get(key) || { team, count: 0, betIds: [] };
      entry.count += 1;
      entry.betIds.push(bet.id);
      byTeam.set(key, entry);
    }
  }
  return byTeam;
}

/**
 * Teams appearing on 2+ pending tickets, worst exposure first
 * (count descending, then team name).
 * Returns [{ team, count, betIds }].
 */
export function findRepeatedTeams(bets) {
  return [...countTicketsByTeam(bets).values()]
    .filter((entry) => entry.count >= 2)
    .sort((a, b) => b.count - a.count || a.team.localeCompare(b.team));
}

/**
 * Exposure of specific teams (e.g. a slip about to be saved) against
 * the user's existing pending tickets: for each named team that is
 * already on 1+ pending tickets, { team, count }. Order follows the
 * input team list, duplicates removed.
 */
export function findExposureForTeams(bets, teams) {
  const byTeam = countTicketsByTeam(bets);
  const out = [];
  const emitted = new Set();
  for (const team of teams || []) {
    const key = normalizeTeamName(team);
    if (!key || emitted.has(key)) continue;
    emitted.add(key);
    const entry = byTeam.get(key);
    if (entry && entry.count >= 1) {
      out.push({ team: entry.team, count: entry.count, betIds: entry.betIds });
    }
  }
  return out;
}

export { normalizeTeamName };
